import { useEffect, useState } from "react";

/** C++ default when no `tiger_max_replay` ini key is present (24 hours). */
export const DEFAULT_MAX_REPLAY_SECS = 24 * 3600;

let cached: number | null = null;
let inFlight: Promise<number> | null = null;

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/**
 * Fetch `tiger_max_replay` (seconds) from the backend `get_tiger_max_replay`
 * command, once, and cache it for the session. The value is global (keyed on
 * host + site config), so a single fetch suffices. Outside Tauri (the dev web
 * server / tests) or on any error it resolves to the 24-hour default.
 */
export async function loadTigerMaxReplay(): Promise<number> {
  if (cached != null) return cached;
  if (inFlight) return inFlight;
  if (!isTauri()) {
    cached = DEFAULT_MAX_REPLAY_SECS;
    return cached;
  }
  inFlight = (async () => {
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const secs = await invoke<number>("get_tiger_max_replay");
      cached = Number.isFinite(secs) && secs > 0 ? secs : DEFAULT_MAX_REPLAY_SECS;
    } catch {
      cached = DEFAULT_MAX_REPLAY_SECS;
    }
    return cached;
  })();
  return inFlight;
}

/**
 * React hook returning `tiger_max_replay` in seconds. Starts at the last-known
 * cached value (or the 24-hour default) and updates once the backend fetch
 * resolves — so duration validation always has a sensible value to compare.
 */
export function useTigerMaxReplay(): number {
  const [secs, setSecs] = useState<number>(cached ?? DEFAULT_MAX_REPLAY_SECS);
  useEffect(() => {
    let alive = true;
    void loadTigerMaxReplay().then((v) => {
      if (alive) setSecs(v);
    });
    return () => {
      alive = false;
    };
  }, []);
  return secs;
}
